// apiError — shared mapper from axios errors to user-meaningful copy.
//
// Every modal routes its catch block through apiErrorMessage so an operator can
// tell apart a 422 validation error, a 409 conflict, a 503 outage, and a 500.
//
// The mapping has three layers:
//   1. Known backend `code` strings → curated user-facing copy.
//   2. HTTP status fallback (404/409/422/429/5xx) for unknown codes.
//   3. The caller-supplied fallback string for anything else (network drop,
//      timeout, CORS preflight failure, etc.).
//
// Extend ERROR_COPY when go-pim adds a new stable `code` rather than
// re-implementing the switch per modal.
import axios, { type AxiosError } from "axios";

import { MalformedJsonResponseError } from "@/api/client";

/**
 * Curated copy for known go-pim backend `code` values returned in the
 * `{ error, code }` envelope.
 */
const ERROR_COPY: Record<string, string> = {
  // Auth / RBAC
  no_claims: "Your session expired. Sign in again.",
  role_required: "You don't have permission for this action.",
  token_signature_invalid: "Your session is invalid. Sign in again.",
  token_expired: "Your session expired. Sign in again.",
  no_bearer: "Your session expired. Sign in again.",

  // Companies (Companies.tsx, CompanyDetail.tsx)
  company_not_found: "Company not found.",
  company_slug_taken: "That identifier is already in use — pick another.",
  company_invalid: "Check the company fields and try again.",
  display_name_invalid: "Display name can't be empty.",
  business_profile_taken:
    "That widget business profile is already linked to another company.",
  document_mode_invalid: "Pick a valid document mode (none, receipt, invoice, or both).",
  taxation_invalid: "Pick a valid taxation scheme (none, gst, or solar_70_30).",

  // Products (Products.tsx)
  product_not_found: "Product not found.",
  product_invalid: "Check the product fields and try again.",
  business_type_invalid:
    "Pick a valid business type (donation, livestock, solar, or generic).",
  tax_scheme_invalid: "Pick a valid tax scheme (none, gst, or solar_70_30).",
  currency_invalid: "Enter a valid ISO currency code.",
  unit_rate_invalid: "Enter a valid unit rate.",

  // Documents (Documents.tsx)
  document_not_found: "No document found for that reference.",
  payment_ref_required: "Enter a payment reference to look up its documents.",

  // Templates
  template_not_found: "That document template is not registered.",

  // Rate limit
  rate_limited:
    "Too many requests — slow down and try again in a few seconds.",

  // Validation
  invalid_request: "The request was rejected as invalid.",
};

/**
 * Best-effort extraction of the backend `code` field from an axios error.
 * Returns `undefined` for non-axios errors and for axios errors without a
 * structured body (network drop, CORS preflight, etc.).
 */
export function extractErrorCode(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  const data = (err as AxiosError).response?.data as
    | { code?: unknown }
    | undefined;
  if (data && typeof data.code === "string") return data.code;
  return undefined;
}

/**
 * HTTP-status fallback when no `code` mapping fires. Keep these generic — if
 * a status has a stable code (e.g. 429 → "rate_limited"), the dict above wins
 * and never reaches here.
 */
function statusMessage(status: number, fallback: string): string {
  if (status === 404) return "Resource not found. It may have been deleted.";
  if (status === 409)
    return "Conflict — that resource already exists or was changed by someone else.";
  if (status === 413)
    return "The request is larger than the server accepts. Split it and try again.";
  if (status === 422) return "Invalid input — check the values and try again.";
  if (status === 429)
    return "Too many requests — slow down and try again in a few seconds.";
  if (status >= 500 && status < 600)
    return "Server error. Try again, or contact support if it persists.";
  return fallback;
}

/**
 * apiErrorMessage maps an axios error (or any thrown value) to a user-facing
 * string. Always returns a non-empty string — never undefined.
 *
 * Precedence (highest to lowest):
 *   1. Known backend `code` → ERROR_COPY[code]
 *   2. HTTP status (4xx/5xx) → statusMessage(status, fallback)
 *   3. Caller-supplied `fallback`
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const code = extractErrorCode(err);
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];

  // MalformedJsonResponseError is thrown by the client's transformResponse
  // when a 2xx body fails JSON.parse — invariably because a misconfigured
  // reverse proxy or CDN intercepted the request and served an HTML error
  // page with status 200. It's a plain Error (not AxiosError) so it bypasses
  // both the code dict and the status fallback without this branch.
  if (err instanceof MalformedJsonResponseError) {
    return "The server's response could not be read — a reverse proxy or CDN may be returning HTML instead of JSON. Refresh, and if it keeps happening, alert ops.";
  }

  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status != null) {
      // Surface the raw code (if any) alongside the status copy so support
      // tickets carry the signal even when we don't have curated copy yet.
      const base = statusMessage(status, fallback);
      if (code) return `${base} (${code})`;
      return base;
    }
    // No response → likely network drop or timeout.
    return "Cannot reach the server. Check your connection and try again.";
  }

  return fallback;
}

/**
 * apiErrorStatus returns the HTTP status of a failed request, or null.
 *
 * Needed because some non-2xx answers are settled facts about CONFIGURATION
 * rather than faults, and each deserves its own screen: 501 means a feature is
 * switched off on this deployment, 400 on the email-template routes means the
 * merchant has no brand and the operator can go and set one. Matching on the
 * message text would break the moment the wording changed.
 */
export function apiErrorStatus(err: unknown): number | null {
  if (err && typeof err === "object" && "response" in err) {
    const resp = (err as { response?: { status?: unknown } }).response;
    if (resp && typeof resp.status === "number") return resp.status;
  }
  return null;
}
